import { BarElement, CategoryScale, Chart as ChartJS, LinearScale, Tooltip } from "chart.js";
import { useCallback, useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

interface BreakdownRow {
  day: string;
  endpoint: string;
  model: string;
  requests: number;
  promptTokens: number;
  candidateTokens: number;
  totalTokens: number;
  avgDurationMs: number | null;
  totalRounds: number;
}

interface DailyTotal {
  day: string;
  requests: number;
  totalTokens: number;
}

interface UsageData {
  breakdown: BreakdownRow[];
  dailyTotals: DailyTotal[];
}

// Gemini API pricing per 1M tokens (as of March 2026)
// https://ai.google.dev/gemini-api/docs/pricing
const MODEL_PRICING: Record<string, { input: number; output: number }> = {
  "gemini-2.5-flash": { input: 0.3, output: 2.5 },
  "gemini-2.5-flash-lite": { input: 0.1, output: 0.4 },
};

const DEFAULT_PRICING = { input: 0.3, output: 2.5 };

function estimateCost(model: string, promptTokens: number, candidateTokens: number): number {
  const pricing = MODEL_PRICING[model] ?? DEFAULT_PRICING;
  return (
    (promptTokens / 1_000_000) * pricing.input + (candidateTokens / 1_000_000) * pricing.output
  );
}

function formatCost(cost: number): string {
  if (cost < 0.005) return `$${cost.toFixed(4)}`;
  if (cost < 1) return `$${cost.toFixed(3)}`;
  return `$${cost.toFixed(2)}`;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

export function AiUsageTab() {
  const [days, setDays] = useState("30");
  const [data, setData] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUsage = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/ai/usage?days=${days}`);
      if (res.ok) {
        setData(await res.json());
      }
    } catch {
      // Silently fail
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const totalRequests = data?.breakdown.reduce((s, r) => s + r.requests, 0) ?? 0;
  const totalTokens = data?.breakdown.reduce((s, r) => s + r.totalTokens, 0) ?? 0;
  const avgTokens = totalRequests > 0 ? Math.round(totalTokens / totalRequests) : 0;
  const totalCost =
    data?.breakdown.reduce(
      (s, r) => s + estimateCost(r.model, r.promptTokens, r.candidateTokens),
      0,
    ) ?? 0;

  const chartData = {
    labels: data?.dailyTotals.map((d) => d.day) ?? [],
    datasets: [
      {
        label: "Total Tokens",
        data: data?.dailyTotals.map((d) => d.totalTokens) ?? [],
        backgroundColor: "#0E3752",
        borderRadius: 4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { maxRotation: 45 },
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value: string | number) => formatNumber(Number(value)),
        },
      },
    },
  };

  // Group breakdown rows by date for visual grouping
  let lastDay = "";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">AI API Usage</h3>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Requests
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <p className="text-2xl font-bold">{totalRequests.toLocaleString()}</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Tokens
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <p className="text-2xl font-bold">{formatNumber(totalTokens)}</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Avg Tokens / Request
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <p className="text-2xl font-bold">{formatNumber(avgTokens)}</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Estimated Cost
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <p className="text-2xl font-bold">{formatCost(totalCost)}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bar chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Daily Token Usage</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-[250px] w-full" />
          ) : data?.dailyTotals.length ? (
            <div className="h-[250px]">
              <Bar data={chartData} options={chartOptions} />
            </div>
          ) : (
            <p className="py-12 text-center text-sm text-muted-foreground">
              No usage data for this period.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Breakdown table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Usage Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={`skel-${i}`} className="h-10 w-full" />
              ))}
            </div>
          ) : data?.breakdown.length ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Endpoint</TableHead>
                    <TableHead>Model</TableHead>
                    <TableHead className="text-right">Requests</TableHead>
                    <TableHead className="text-right">Prompt Tokens</TableHead>
                    <TableHead className="text-right">Candidate Tokens</TableHead>
                    <TableHead className="text-right">Total Tokens</TableHead>
                    <TableHead className="text-right">Est. Cost</TableHead>
                    <TableHead className="text-right">Avg Duration</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.breakdown.map((row, i) => {
                    const showDate = row.day !== lastDay;
                    lastDay = row.day;
                    const rowCost = estimateCost(row.model, row.promptTokens, row.candidateTokens);
                    return (
                      <TableRow key={`${row.day}-${row.endpoint}-${row.model}-${i}`}>
                        <TableCell className="font-medium">{showDate ? row.day : ""}</TableCell>
                        <TableCell>
                          <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                            {row.endpoint}
                          </code>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{row.model}</TableCell>
                        <TableCell className="text-right">{row.requests}</TableCell>
                        <TableCell className="text-right">
                          {row.promptTokens.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {row.candidateTokens.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {row.totalTokens.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCost(rowCost)}
                        </TableCell>
                        <TableCell className="text-right">
                          {row.avgDurationMs != null
                            ? `${Math.round(row.avgDurationMs)}ms`
                            : "\u2014"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No usage data for this period.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Pricing reference */}
      <p className="text-xs text-muted-foreground">
        Cost estimates based on published Gemini API rates: Flash ($0.30/$2.50 per 1M input/output
        tokens), Flash-Lite ($0.10/$0.40 per 1M input/output tokens). Actual billing may vary.
      </p>
    </div>
  );
}
